<template>
  <div v-if="hasContent" class="collapsible-info" :class="variantClass">
    <div class="d-flex align-items-start">
      <font-awesome-icon :icon="['fal', iconName]" class="mr-2 mt-1" :class="iconColorClass" />
      <div class="flex-grow-1 small">
        <div class="d-flex align-items-center justify-content-between mb-1">
          <div class="text-muted">{{ title }}</div>
          <b-button v-if="description && canToggle" @click.stop.prevent="toggleCollapse" :variant="buttonVariant" size="sm">
            <font-awesome-icon :icon="['fal', isCollapsed ? 'chevron-down' : 'chevron-up']" class="mr-1" />
            {{ isCollapsed ? $t('Webportal.Actions.InquiryWizard.ShowMore') : $t('Webportal.Actions.InquiryWizard.ShowLess') }}
          </b-button>
        </div>
        <div v-if="subTitle" class="font-weight-bold mb-2">
          {{ subTitle }}
        </div>

        <!-- Collapsible description -->
        <template v-if="canToggle">
          <b-collapse :visible="!isCollapsed">
            <div>{{ description }}</div>
          </b-collapse>
        </template>
        <!-- Static description (when no toggle) -->
        <div v-else-if="description">{{ description }}</div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CollapsibleInfo",

  props: {
    iconName: {
      type: String,
      default: "info-circle",
    },
    /** Main header text — always visible when present. */
    title: {
      type: String,
      default: null,
    },
    /** Subtitle text — always visible when present. */
    subTitle: {
      type: String,
      default: null,
    },
    /** Detail text — collapsible when showToggle is true. */
    description: {
      type: String,
      default: null,
    },
    /** Visual variant: 'default' (light bg, dark border) or 'info' (info bg, info border). */
    variant: {
      type: String,
      default: "default",
      validator: (v) => ["default", "info"].includes(v),
    },
    showToggle: {
      type: Boolean,
      default: false,
    },
    initialCollapsed: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      isCollapsed: this.initialCollapsed,
    };
  },

  computed: {
    hasContent() {
      return !!(this.title || this.subTitle || this.description);
    },

    canToggle() {
      return this.showToggle && !!this.description;
    },

    iconColorClass() {
      return this.variant === "info" ? "text-info" : "";
    },

    buttonVariant() {
      return this.variant === "info" ? "outline-info" : "outline-secondary";
    },

    variantClass() {
      return `collapsible-info--${this.variant}`;
    },
  },

  watch: {
    initialCollapsed(newVal) {
      this.isCollapsed = newVal;
    },
  },

  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      this.$emit("toggle", this.isCollapsed);
    },
  },
};
</script>

<style lang="scss" scoped>
.collapsible-info {
  padding: 0.75rem 1rem;
  border-radius: 0.25rem;
  border-left: 3px solid;
  overflow-wrap: break-word;
  word-break: break-word;

  &--default {
    background-color: var(--light);
    border-left-color: var(--dark);
  }

  &--info {
    background-color: var(--info-55);
    border-left-color: var(--info);
  }
}
</style>
